import type { SectionSettings, SectionType } from "@veo/types";
import { cn } from "@veo/ui";
import type { ReactNode } from "react";

export interface SectionProps<TContent = Record<string, unknown>> {
  content: TContent;
  settings: SectionSettings;
  /**
   * Content this block shows but does not own — services, articles, FAQs,
   * testimonials, team. Fetched by the route loader so it is present in the
   * server-rendered HTML rather than appearing a moment later.
   */
  data?: unknown;
}

/**
 * The editor's choices, mapped onto the surface scale.
 *
 * The keys are the vocabulary an editor already knows and are stored against
 * every section, so they stay as they are; what each one resolves to is a
 * presentation decision that belongs here. "white" lifts to a true panel above
 * the page ground, and "cream" now reads as a cool band rather than a beige
 * one — the same alternation, a cleaner finish.
 */
const BACKGROUNDS: Record<string, string> = {
  white: "bg-surface-0 textured text-ink-800",
  cream: "bg-surface-100 textured text-ink-800",
  navy: "bg-navy-900 textured-dark text-navy-100",
  deepBlue: "bg-navy-700 textured-dark text-navy-100",
  goldTint: "bg-gold-50 textured text-ink-800",
  // The block paints a photograph across this one; grain underneath it would
  // never be seen, and grain over it would fight the image.
  image: "bg-navy-900 text-navy-100",
};

/**
 * Top and bottom spelled out separately, rather than one `py-` scale rewritten
 * into `pt-`/`pb-` at runtime.
 *
 * Tailwind generates utilities by scanning source text for class names it can
 * see. A class assembled at runtime — `"py-16".replace("py-", "pt-")` — is
 * invisible to that scan, so the CSS was never emitted and every section
 * rendered with no vertical padding at all. Written out in full, each class is
 * literal, greppable, and actually exists in the stylesheet.
 */
const PADDING_TOP: Record<string, string> = {
  none: "pt-0",
  compact: "pt-10",
  normal: "pt-16 md:pt-24",
  generous: "pt-24 md:pt-32",
  spacious: "pt-32 md:pt-44",
};

const PADDING_BOTTOM: Record<string, string> = {
  none: "pb-0",
  compact: "pb-10",
  normal: "pb-16 md:pb-24",
  generous: "pb-24 md:pb-32",
  spacious: "pb-32 md:pb-44",
};

const WIDTHS: Record<string, string> = {
  narrow: "max-w-2xl",
  standard: "max-w-5xl",
  wide: "max-w-7xl",
  full: "max-w-none",
};

/**
 * The shell every block renders inside.
 *
 * Background, spacing, alignment and container width are applied here rather
 * than in each of the seventeen renderers. A block author writes content markup;
 * the presentation an editor controls is handled once, consistently.
 */
export function SectionShell({
  settings,
  children,
  className,
}: {
  settings: SectionSettings;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section
      // The anchor lets navigation link straight to a section.
      id={settings.anchorId || undefined}
      className={cn(
        BACKGROUNDS[settings.background] ?? BACKGROUNDS.white,
        PADDING_TOP[settings.paddingTop] ?? PADDING_TOP.normal,
        PADDING_BOTTOM[settings.paddingBottom] ?? PADDING_BOTTOM.normal,
        settings.customClassName,
        className,
      )}
    >
      <div
        className={cn(
          "mx-auto w-full px-5 sm:px-8 lg:px-10",
          WIDTHS[settings.containerWidth] ?? WIDTHS.standard,
          settings.alignment === "center" && "text-center",
          settings.alignment === "right" && "text-right",
        )}
      >
        {children}
      </div>
    </section>
  );
}

/** Heading group shared by most blocks, so eyebrow/heading spacing is uniform. */
export function SectionHeading({
  eyebrow,
  heading,
  intro,
  settings,
  as = "h2",
  size = "section",
}: {
  eyebrow?: string;
  heading?: string;
  intro?: string;
  settings: SectionSettings;
  as?: "h1" | "h2";
  /**
   * `section` is the standard weight for a block that owns its stretch of the
   * page. `prose` drops it to the size rich text sets an h2 at, for a block
   * slotted into a run of prose headings — the motto between a vision and a
   * mission — where the standard size reads as a louder heading than its
   * neighbours rather than as one of them.
   */
  size?: "section" | "prose";
}) {
  if (!eyebrow && !heading && !intro) return null;

  const onDark =
    settings.background === "navy" ||
    settings.background === "deepBlue" ||
    settings.background === "image";
  const Tag = as;

  return (
    <div className={cn("mb-12", settings.alignment === "center" && "mx-auto max-w-2xl")}>
      {eyebrow && (
        <p
          className={cn(
            "text-xs font-semibold uppercase tracking-wider",
            onDark ? "text-gold-300" : "text-gold-600",
          )}
        >
          {eyebrow}
        </p>
      )}
      {heading && (
        <Tag
          className={cn(
            "mt-3",
            size === "prose" ? "text-3xl" : "text-4xl",
            onDark ? "text-cream-50" : "text-navy-900",
          )}
        >
          {heading}
        </Tag>
      )}
      {heading && (
        <span
          aria-hidden
          className={cn(
            "mt-4 block h-0.5 w-12 bg-gold-500",
            settings.alignment === "center" && "mx-auto",
          )}
        />
      )}
      {intro && (
        <p
          className={cn("mt-5 text-lg leading-relaxed", onDark ? "text-navy-200" : "text-ink-600")}
        >
          {intro}
        </p>
      )}
    </div>
  );
}

/**
 * The hover treatment shared by every content card.
 *
 * One definition rather than a hover class per block, because four blocks each
 * inventing their own "slightly stronger shadow" is how a page ends up feeling
 * assembled from parts. The services grid already used this language — a lift
 * on light grounds, a gold edge on dark — so it is lifted here rather than
 * invented.
 *
 * Deliberately no transform. A card that moves under the pointer implies it can
 * be clicked, and several of these — a testimonial, a team member without a
 * profile — cannot be. A shadow and an edge read as "you are here" rather than
 * "press me".
 *
 * `hover:` only applies where a real pointer exists, so touch devices are
 * unaffected; `motion-reduce` drops the transition to an instant state change
 * rather than removing the feedback altogether.
 */
export function cardHover(onDark: boolean): string {
  return cn(
    "transition-[box-shadow,border-color,background-color] duration-300 motion-reduce:transition-none",
    onDark
      ? "hover:border-gold-500/50 hover:bg-navy-800/60"
      : "hover:border-ink-300 hover:shadow-lg",
  );
}

export function isDarkBackground(settings: SectionSettings): boolean {
  return ["navy", "deepBlue", "image"].includes(settings.background);
}

export interface RenderedSection {
  id: string;
  sectionType: SectionType;
  content: unknown;
  settings: SectionSettings;
}
